import { useEffect, useId, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronDown, Cookie, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/context/I18nContext";
import { readConsent, writeConsent, CONSENT_EVENT } from "@/lib/consent";

/**
 * Consent prompt for analytics tracking.
 *
 * Deliberately not a modal and not a full-screen overlay: neither KVKK nor
 * GDPR requires blocking the page, and a wall in front of the content pressures
 * people into accepting to get past it — which undermines the "freely given"
 * requirement it is supposed to satisfy.
 *
 * Accept and decline carry equal visual weight for the same reason. A greyed
 * out "decline" next to a bright "accept all" is the pattern regulators
 * single out.
 *
 * Renders nothing until mounted, so the server never guesses at a decision it
 * cannot read.
 */
export function ConsentBanner() {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);
  // Collapsed by default. The two top-level buttons are a complete, symmetrical
  // choice on their own, so most visitors never need this; opening it is for
  // the visitor who wants to accept one purpose and not the other.
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const detailsId = useId();

  useEffect(() => {
    // A stored decision from an older CONSENT_VERSION reads as null, which
    // re-prompts — correct, since the categories it agreed to have changed.
    if (readConsent() === null) setVisible(true);
    const onChange = () => setVisible(readConsent() === null);
    window.addEventListener(CONSENT_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_EVENT, onChange);
  }, []);

  // The banner, the WhatsApp launcher and (when mounted) the quote bar are three
  // independent fixed elements that all anchor to the bottom of the viewport and
  // none of which knew about the others — on a phone the banner simply buried the
  // launcher. Rather than invent a second coordination scheme, this reuses the
  // body-class mechanism SiteHeader already uses for `mobile-nav-open`.
  useEffect(() => {
    document.body.classList.toggle("consent-open", visible);
    return () => document.body.classList.remove("consent-open");
  }, [visible]);

  if (!visible) return null;

  const decide = (analyticsChoice: boolean, marketingChoice: boolean) => {
    writeConsent(analyticsChoice, marketingChoice);
    setVisible(false);
  };

  return (
    <div
      // role="region" rather than "dialog": it does not trap focus, and
      // announcing it as a dialog would imply the page is blocked.
      role="region"
      aria-label={t("consent.aria.label")}
      className="fixed inset-x-0 bottom-0 z-50 border-t border-border/80 bg-background/98 backdrop-blur-md shadow-[0_-8px_32px_-12px_rgba(0,30,64,0.25)] [padding-bottom:env(safe-area-inset-bottom)]"
    >
      <div className="mx-auto max-w-7xl px-4 py-4 md:px-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 hidden shrink-0 rounded-xl bg-primary/10 p-2 text-primary sm:block">
              <Cookie className="h-4 w-4" aria-hidden="true" />
            </span>
            <p className="text-xs leading-relaxed text-muted-foreground">
              {t("consent.body")}{" "}
              <Link to="/privacy" className="font-bold text-primary hover:text-navy">
                {t("consent.privacyLink")}
              </Link>
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {/* Equal weight: same size, same shape. Accept-all and reject-all
                are both one click from here, which is the requirement — a
                "reject" that costs more clicks than "accept" is the pattern
                regulators single out. */}
            <Button
              variant="outline"
              className="rounded-xl text-xs font-bold"
              onClick={() => decide(false, false)}
            >
              {t("consent.decline")}
            </Button>
            <Button className="rounded-xl text-xs font-bold" onClick={() => decide(true, true)}>
              {t("consent.acceptAll")}
            </Button>
            {/* Dismissing without choosing is a refusal, not a deferral — we must
                not collect while waiting for someone to decide later. */}
            <button
              type="button"
              onClick={() => decide(false, false)}
              aria-label={t("consent.dismiss")}
              className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-surface-alt hover:text-navy"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Per-category choice. A plain disclosure rather than a modal: opening
            it must not feel like a penalty for wanting to choose. */}
        <div className="mt-3 border-t border-border/60 pt-3">
          <button
            type="button"
            onClick={() => setDetailsOpen((open) => !open)}
            aria-expanded={detailsOpen}
            aria-controls={detailsId}
            className="inline-flex items-center gap-1.5 text-[11px] font-bold text-muted-foreground transition-colors hover:text-navy"
          >
            <ChevronDown
              className={`h-3.5 w-3.5 transition-transform ${detailsOpen ? "rotate-180" : ""}`}
              aria-hidden="true"
            />
            {t("consent.customise")}
          </button>

          <div id={detailsId} hidden={!detailsOpen} className="mt-3 space-y-3">
            {/* Listed but not switchable, and said plainly rather than shown as
                a disabled toggle pretending to be a choice. */}
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              <span className="font-bold text-navy">{t("consent.cat.necessary")}</span>{" "}
              {t("consent.cat.necessary.body")}
            </p>

            <label className="flex cursor-pointer items-start gap-2.5">
              <input
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-primary"
              />
              <span className="text-[11px] leading-relaxed text-muted-foreground">
                <span className="font-bold text-navy">{t("consent.cat.analytics")}</span>{" "}
                {t("consent.cat.analytics.body")}
              </span>
            </label>

            <label className="flex cursor-pointer items-start gap-2.5">
              <input
                type="checkbox"
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
                className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-primary"
              />
              <span className="text-[11px] leading-relaxed text-muted-foreground">
                <span className="font-bold text-navy">{t("consent.cat.marketing")}</span>{" "}
                {t("consent.cat.marketing.body")}
              </span>
            </label>

            <Button
              variant="outline"
              className="rounded-xl text-xs font-bold"
              onClick={() => decide(analytics, marketing)}
            >
              {t("consent.saveChoices")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
